// Staff Billing area API hooks. Deliberately separate from the client-facing
// services/billing.js: that file owns ['billing-invoices', claimId] against
// the claim-scoped GET /billing/invoices, so the staff endpoints get their own
// namespaced keys and can never cross-invalidate by accident.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

export const useFetchInvoiceTiles = (opts = {}) =>
  useQuery(
    ['billing-invoice-tiles'],
    async () => {
      const res = await axios.get('/billing/admin/invoices/tiles');
      return res.data;
    },
    { refetchOnWindowFocus: false, staleTime: 30000, ...opts },
  );

// Billing Schedule slide-over: projected reminder/plan-offer sends + real
// installment charges over the next 30 days (forward mode), or the real
// record of past sends/charges/payments (history mode, params.start set).
// Param-keyed so history windows and the forward view cache separately.
// staleTime 0: the panel promises "recalculated every time this opens", so
// every mount refetches — a dispute hold placed seconds ago must show as
// held immediately.
export const useFetchBillingSchedule = (params = {}, opts = {}) =>
  useQuery(
    ['billing-schedule', params],
    async () => {
      const res = await axios.get('/billing/admin/invoices/schedule', {
        params,
      });
      return res.data;
    },
    { refetchOnWindowFocus: false, staleTime: 0, ...opts },
  );

export const useFetchAdminInvoices = (params, opts = {}) =>
  useQuery(
    ['billing-admin-invoices', params],
    async () => {
      const res = await axios.get('/billing/admin/invoices', { params });
      return res.data;
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 5000,
      ...opts,
    },
  );

// Export fetch (not a hook): the whole filtered set in one page (server caps
// at 5000 rows) for the client-side CSV build. Same endpoint, same filters,
// so the export always matches exactly what the table shows.
export const fetchAdminInvoicesExport = async (params = {}) => {
  const res = await axios.get('/billing/admin/invoices', {
    params: { ...params, exportAll: 1, page: undefined, pageSize: undefined },
  });
  return res.data;
};

export const useUpdateInvoiceNotes = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ invoiceId, notes }) => {
      const res = await axios.patch(
        `/billing/admin/invoices/${invoiceId}/notes`,
        { notes },
      );
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['billing-admin-invoices']);
      },
    },
  );
};

export const useFetchInvoiceEmails = (invoiceId, opts = {}) =>
  useQuery(
    ['billing-invoice-emails', invoiceId],
    async () => {
      const res = await axios.get(
        `/billing/admin/invoices/${invoiceId}/emails`,
      );
      return res.data;
    },
    { enabled: Boolean(invoiceId), refetchOnWindowFocus: false, ...opts },
  );

export const useFetchBillingSettings = (opts = {}) =>
  useQuery(
    ['billing-admin-settings'],
    async () => {
      const res = await axios.get('/billing/admin/settings');
      return res.data;
    },
    { refetchOnWindowFocus: false, staleTime: 30000, ...opts },
  );

export const useUpdateBillingSettings = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (patch) => {
      const res = await axios.put('/billing/admin/settings', patch);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['billing-admin-settings']);
        queryClient.invalidateQueries(['billing-invoice-tiles']);
      },
    },
  );
};

export const useFetchBillingSuppressions = (opts = {}) =>
  useQuery(
    ['billing-suppressions'],
    async () => {
      const res = await axios.get('/billing/admin/suppressions');
      return res.data;
    },
    { refetchOnWindowFocus: false, ...opts },
  );

export const useAddBillingSuppression = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ email, reason }) => {
      const res = await axios.post('/billing/admin/suppressions', {
        email,
        reason,
      });
      return res.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['billing-suppressions']),
    },
  );
};

export const useRemoveBillingSuppression = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (suppressionId) => {
      const res = await axios.delete(
        `/billing/admin/suppressions/${suppressionId}`,
      );
      return res.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['billing-suppressions']),
    },
  );
};

const useInvoiceReminderAction = (path) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (invoiceId) => {
      const res = await axios.post(
        `/billing/admin/invoices/${invoiceId}/reminders/${path}`,
      );
      return res.data;
    },
    {
      onSuccess: (_data, invoiceId) => {
        queryClient.invalidateQueries(['billing-admin-invoices']);
        queryClient.invalidateQueries(['billing-invoice-emails', invoiceId]);
        queryClient.invalidateQueries(['billing-invoice-tiles']);
      },
    },
  );
};

export const usePauseInvoiceReminders = () => useInvoiceReminderAction('pause');
export const useResumeInvoiceReminders = () =>
  useInvoiceReminderAction('resume');
export const useSendInvoiceReminderNow = () =>
  useInvoiceReminderAction('send-now');
